import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactSwitch from 'react-switch';
import { closeChildMissingTermsPopup } from '~/actions';
import { OutlineWarning01Icon } from '@tempmee/react-icons';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { ConfirmPopup, Row } from '~/commonComponents';
import { useAcceptGoTuTerms } from '~/domain/AcceptGoTuTerms';
import { useAcceptPSATerms } from '~/domain/AcceptPSATerms';
import { useAppSelector } from '~/hooks/useAppSelector';
import { getPopupType, POPUP_CONFIGS, renderSwitchText } from './utils';

export const ShowChildMissingTermsPopup = () => {
  const dispatch = useDispatch();
  const { isTCMissing, isPSAMissing, officeId } = useAppSelector((state) => state.multiday);
  const [isAccepted, setIsAccepted] = useState(false);
  const acceptPSATermsMutation = useAcceptPSATerms();
  const acceptGoTuTermsMutation = useAcceptGoTuTerms();

  const popupType = useMemo(
    () => getPopupType(isTCMissing, isPSAMissing),
    [isTCMissing, isPSAMissing],
  );
  const popupConfig = popupType ? POPUP_CONFIGS[popupType] : null;

  const isLoading = acceptPSATermsMutation.isPending || acceptGoTuTermsMutation.isPending;

  const handleToggleSwitch = useCallback(() => {
    setIsAccepted((prev) => !prev);
  }, []);

  const handleOnPress = useCallback(async () => {
    if (!popupType || !officeId) return;

    switch (popupType) {
      case 'psa':
        await acceptPSATermsMutation.mutateAsync({ officeId });
        break;
      case 'gotuTerms':
        await acceptGoTuTermsMutation.mutateAsync({ officeId });
        break;
      case 'both':
        await Promise.all([
          acceptGoTuTermsMutation.mutateAsync({ officeId }),
          acceptPSATermsMutation.mutateAsync({ officeId }),
        ]);
        break;
      default:
        break;
    }

    dispatch(closeChildMissingTermsPopup());
  }, [popupType, officeId, acceptGoTuTermsMutation, acceptPSATermsMutation, dispatch]);

  if (!popupConfig) return null;

  return (
    <ConfirmPopup
      Icon={() => <OutlineWarning01Icon size={80} color={colors.warning[500]} />}
      title={popupConfig.title}
      titleStyle={{
        marginTop: 24,
      }}
      description={popupConfig.description}
      descriptionStyle={{
        color: colors.neutral[500],
      }}
      rightButtonText="Confirm"
      rightButtonAction={handleOnPress}
      rightButtonLoading={isLoading}
      rightButtonDisabled={isLoading || !isAccepted}
    >
      <Row
        alignItems="center"
        gap={16}
        marginTop={-32}
        marginBottom={32}
        paddingHorizontal={40}
        justifyContent="center"
      >
        <ReactSwitch
          checked={isAccepted}
          onChange={handleToggleSwitch}
          onColor={colors.secondary[500]}
          onHandleColor="#fff"
          handleDiameter={20}
          uncheckedIcon={false}
          checkedIcon={false}
          offColor={colors.neutral[100]}
          boxShadow="0px 1px 5px rgba(0, 0, 0, 0.35)"
          activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
          height={25}
          width={50}
          className="react-switch"
          id="material-switch"
        />
        <Typography as="span" color={colors.neutral[500]}>{renderSwitchText(popupConfig.type, popupConfig.links)}</Typography>
      </Row>
    </ConfirmPopup>
  );
};
